import * as React from 'react';

import { InfoIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Divider, Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  AppliedSellRateRule,
  BuyRateData,
  BuyRateRule,
  PricingFormula,
  QQAgentInvocation,
  SellRateCalculationLaneInvocation,
  SellRatePricingMode,
  SellRateRoundingRules,
} from 'types/agents/QuickQuoteAgent';

import { formatDollarsNoCents, toTitleCase } from '../utils';
import {
  BuyRateQuoteRangeRow,
  BuyRateRangePercentileRuleBlock,
  BuyRateRuleRow,
} from './BuyRateRuleRow';
import { SellRateRuleBreakdown } from './SellRateRuleBreakdown';
import {
  type BuyRateBreakdownState,
  formatLaneRoute,
  formatLaneRouteShort,
  getBuyRateBreakdownState,
  getDisplaySellValue,
  getFixedFinalPricingMathLabel,
  getLaneTooltipContent,
  getQuoteSourceDisplay,
  isFixedFinalPricing,
} from './helpers';

interface BatchLaneDescriptionProps {
  buyInvocation: BuyRateData & { lanes: NonNullable<BuyRateData['lanes']> };
  sellRateTasks: QQAgentInvocation['taskInvocations'];
}

type BuyLane = NonNullable<BuyRateData['lanes']>[number];

interface LaneRouteSummaryProps {
  route: string;
  routeShort: string;
  hasMultipleStops: boolean;
}

interface LaneSummaryTextProps {
  isFixedFinalLane: boolean;
  selectedQuoteSource: string;
  buyRate: Undef<number>;
  hasSellRate: boolean;
  sellRateValue: Undef<number>;
}

interface BuyRateBreakdownSectionProps {
  originalBuyRate: Undef<number>;
  selectedQuoteSource: string;
  buyRateRangeLow: Undef<number>;
  buyRateRangeHigh: Undef<number>;
  buyRateRules: BuyRateRule[];
  buyRate: Undef<number>;
  breakdownState: BuyRateBreakdownState;
}

interface SellRateBreakdownSectionProps {
  hasSellRate: boolean;
  buyRate: Undef<number>;
  sellRate: Undef<number>;
  sellRateRounded: Undef<number>;
  appliedSellRateRule: Undef<AppliedSellRateRule>;
  marginType: Undef<string>;
  marginValue: Undef<number>;
  pricingFormula: Undef<PricingFormula>;
  pricingMode: Undef<SellRatePricingMode>;
  roundingRules: Undef<SellRateRoundingRules>;
}

interface LaneDetailsTooltipProps {
  route: string;
  tooltipContent: string;
  buyRate: Undef<number>;
  originalBuyRate: Undef<number>;
  selectedQuoteSource: string;
  buyRateRangeLow: Undef<number>;
  buyRateRangeHigh: Undef<number>;
  buyRateRules: BuyRateRule[];
  buyRateBreakdownState: BuyRateBreakdownState;
  hasSellRate: boolean;
  sellRate: Undef<number>;
  sellRateRounded: Undef<number>;
  appliedSellRateRule: Undef<AppliedSellRateRule>;
  marginType: Undef<string>;
  marginValue: Undef<number>;
  pricingFormula: Undef<PricingFormula>;
  pricingMode: Undef<SellRatePricingMode>;
  roundingRules: Undef<SellRateRoundingRules>;
}

const LaneRouteSummary = ({
  route,
  routeShort,
  hasMultipleStops,
}: LaneRouteSummaryProps): Maybe<React.JSX.Element> => {
  if (!route) {
    return null;
  }

  return (
    <Typography variant='body-xs' weight='medium' as='div'>
      {hasMultipleStops ? (
        <Tooltip delayDuration={10}>
          <TooltipTrigger asChild>
            <Typography
              variant='body-xs'
              weight='medium'
              as='span'
              className='cursor-help underline decoration-dotted decoration-neutral-500 underline-offset-2'
            >
              {routeShort}
            </Typography>
          </TooltipTrigger>
          <TooltipContent className='max-w-64 p-2 ml-1'>
            <Typography
              variant='body-xs'
              className='whitespace-normal wrap-break-word'
            >
              {route}
            </Typography>
          </TooltipContent>
        </Tooltip>
      ) : (
        route
      )}
    </Typography>
  );
};

const LaneSummaryText = ({
  isFixedFinalLane,
  selectedQuoteSource,
  buyRate,
  hasSellRate,
  sellRateValue,
}: LaneSummaryTextProps): React.JSX.Element => {
  if (isFixedFinalLane && hasSellRate) {
    return (
      <>
        Sell{' '}
        <Typography variant='body-xs' weight='semibold' as='span'>
          {formatDollarsNoCents(sellRateValue)}
        </Typography>
      </>
    );
  }

  return (
    <>
      {selectedQuoteSource ? (
        <>
          <Typography variant='body-xs' weight='semibold' as='span'>
            {getQuoteSourceDisplay(selectedQuoteSource)}
          </Typography>
          :
        </>
      ) : (
        ':'
      )}{' '}
      Buy{' '}
      <Typography variant='body-xs' weight='medium' as='span'>
        {buyRate !== undefined ? formatDollarsNoCents(buyRate) : 'N/A'}
      </Typography>
      {hasSellRate && (
        <>
          {' '}
          → Sell{' '}
          <Typography variant='body-xs' weight='medium' as='span'>
            {formatDollarsNoCents(sellRateValue)}
          </Typography>
        </>
      )}
    </>
  );
};

const BuyRateBreakdownSection = ({
  originalBuyRate,
  selectedQuoteSource,
  buyRateRangeLow,
  buyRateRangeHigh,
  buyRateRules,
  buyRate,
  breakdownState,
}: BuyRateBreakdownSectionProps): Maybe<React.JSX.Element> => {
  if (!breakdownState.hasBuyRateBreakdown) {
    return null;
  }

  return (
    <Flex direction='col' gap='xs' className='mt-1  w-full'>
      <Divider size='sm' spacing='xs' />
      {originalBuyRate != null && (
        <Flex align='center' justify='between' className='w-full gap-3'>
          <Typography
            variant='body-xxs'
            textColor='lessMuted'
            className='min-w-0 flex-1 leading-snug'
          >
            Original{' '}
            {getQuoteSourceDisplay(selectedQuoteSource) !== '—'
              ? `${getQuoteSourceDisplay(selectedQuoteSource)} `
              : ''}
            Rate Estimate
          </Typography>
          <Typography
            variant='body-xxs'
            weight='medium'
            className='shrink-0 tabular-nums'
          >
            {formatDollarsNoCents(originalBuyRate)}
          </Typography>
        </Flex>
      )}
      {breakdownState.showStandaloneQuoteRange && (
        <BuyRateQuoteRangeRow
          low={buyRateRangeLow}
          high={buyRateRangeHigh}
          targetBuyRate={
            breakdownState.showMetadataTargetBuyInRange ? buyRate : undefined
          }
        />
      )}
      {buyRateRules.map((rule, idx) =>
        rule.adjustmentType === 'rangePercentile' ? (
          <BuyRateRangePercentileRuleBlock
            key={idx}
            rule={rule}
            rangeLow={buyRateRangeLow}
            rangeHigh={buyRateRangeHigh}
          />
        ) : (
          <BuyRateRuleRow key={idx} rule={rule} />
        )
      )}
      {breakdownState.showFinalBuyRateRow && (
        <Flex align='center' justify='between' className='w-full mt-1'>
          <Typography variant='body-xxs' weight='semibold'>
            Final Buy Rate
          </Typography>
          <Typography variant='body-xxs' weight='semibold'>
            {formatDollarsNoCents(buyRate)}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
};

const SellRateBreakdownSection = ({
  hasSellRate,
  buyRate,
  sellRate,
  sellRateRounded,
  appliedSellRateRule,
  marginType,
  marginValue,
  pricingFormula,
  pricingMode,
  roundingRules,
}: SellRateBreakdownSectionProps): Maybe<React.JSX.Element> => {
  if (!hasSellRate) {
    return null;
  }

  return (
    <Flex direction='col' gap='xs' className='mt-1 w-full'>
      <Divider size='sm' spacing='xs' />
      <SellRateRuleBreakdown
        buyRate={buyRate}
        sellRate={sellRate}
        sellRateRounded={sellRateRounded}
        appliedSellRateRule={appliedSellRateRule}
        marginType={marginType}
        marginValue={marginValue}
        pricingFormula={pricingFormula}
        pricingMode={pricingMode}
        roundingRules={roundingRules}
      />
    </Flex>
  );
};

const LaneDetailsTooltip = ({
  route,
  tooltipContent,
  buyRate,
  originalBuyRate,
  selectedQuoteSource,
  buyRateRangeLow,
  buyRateRangeHigh,
  buyRateRules,
  buyRateBreakdownState,
  hasSellRate,
  sellRate,
  sellRateRounded,
  appliedSellRateRule,
  marginType,
  marginValue,
  pricingFormula,
  pricingMode,
  roundingRules,
}: LaneDetailsTooltipProps): React.JSX.Element => {
  return (
    <TooltipContent className='max-w-72 p-2 mr-2'>
      <Flex direction='col' gap='xs' className='w-full'>
        <Typography variant='body-xs' weight='semibold'>
          {route ? `${route}. ` : ''}
        </Typography>
        <Typography variant='body-xs' textColor='lessMuted'>
          {tooltipContent}
        </Typography>
        <BuyRateBreakdownSection
          originalBuyRate={originalBuyRate}
          selectedQuoteSource={selectedQuoteSource}
          buyRateRangeLow={buyRateRangeLow}
          buyRateRangeHigh={buyRateRangeHigh}
          buyRateRules={buyRateRules}
          buyRate={buyRate}
          breakdownState={buyRateBreakdownState}
        />
        <SellRateBreakdownSection
          hasSellRate={hasSellRate}
          buyRate={buyRate}
          sellRate={sellRate}
          sellRateRounded={sellRateRounded}
          appliedSellRateRule={appliedSellRateRule}
          marginType={marginType}
          marginValue={marginValue}
          pricingFormula={pricingFormula}
          pricingMode={pricingMode}
          roundingRules={roundingRules}
        />
      </Flex>
    </TooltipContent>
  );
};

export const BatchLaneDescription = ({
  buyInvocation,
  sellRateTasks,
}: BatchLaneDescriptionProps): React.JSX.Element => {
  const buyLanes = buyInvocation.lanes ?? [];
  const sellBatch = sellRateTasks
    .filter((task) => task.metadata?.sellRateCalculationTaskInvocation)
    .sort((a, b) => a.step - b.step)
    .find(
      (task) =>
        task.metadata?.sellRateCalculationTaskInvocation?.mode === 'batch'
    )?.metadata?.sellRateCalculationTaskInvocation;

  const sellLanesByQuoteRequestId = new Map<
    number,
    SellRateCalculationLaneInvocation
  >((sellBatch?.lanes ?? []).map((lane) => [lane.quoteRequestId, lane]));

  const laneCount = buyInvocation.laneCount ?? buyLanes.length;

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      <Typography variant='body-xs' weight='medium'>
        Quoted {laneCount} lanes and drafted 1 email.
      </Typography>

      <Flex
        direction='col'
        gap='sm'
        className='pl-2 border-l-2 border-neutral-400'
      >
        {buyLanes.map((lane: BuyLane, index) => {
          const laneNumber = index + 1;
          const sellLane = sellLanesByQuoteRequestId.get(lane.quoteRequestId);
          const laneError = lane.error || sellLane?.error;

          const stops = lane.stops || sellLane?.stops;
          const route = formatLaneRoute(stops);
          const routeShort = formatLaneRouteShort(stops);
          const hasMultipleStops = (stops?.length ?? 0) > 2;
          const transportType = (
            lane.transportType ||
            sellLane?.transportType ||
            ''
          )
            .toString()
            .trim();
          const selectedQuoteSource = (lane.selectedQuoteSource || '')
            .toString()
            .trim();

          const buyRate = lane.buyRate;
          const originalBuyRate = lane.originalBuyRate;
          const buyRateRangeLow = lane.buyRateRangeLow;
          const buyRateRangeHigh = lane.buyRateRangeHigh;
          const buyRateRules = lane.appliedRules ?? [];
          const buyRateBreakdownState = getBuyRateBreakdownState(
            buyRateRules,
            buyRateRangeLow,
            buyRateRangeHigh
          );

          const sellRateValue =
            getDisplaySellValue(
              sellLane?.sellRateRounded,
              sellLane?.sellRate
            ) ?? undefined;

          const hasSellRate = sellRateValue != null && !laneError;

          const appliedSellRateRule =
            sellLane?.appliedSellRateRule ?? sellBatch?.appliedSellRateRule;
          const laneRoundingRules =
            sellLane?.sellRateRoundingRules ?? sellBatch?.sellRateRoundingRules;
          const laneMarginType = sellLane?.marginType ?? sellBatch?.marginType;
          const laneMarginValue =
            sellLane?.marginValue ?? sellBatch?.marginValue;
          const lanePricingFormula =
            sellLane?.pricingFormula ?? sellBatch?.pricingFormula;
          const lanePricingMode =
            sellLane?.pricingMode ?? sellBatch?.pricingMode;
          const isFixedFinalLane = isFixedFinalPricing({
            pricingMode: lanePricingMode,
            appliedSellRateRule,
            marginType: laneMarginType,
            marginValue: laneMarginValue,
            pricingFormula: lanePricingFormula,
            sellRate: sellLane?.sellRate,
            buyRate,
          });
          const fixedFinalMathLabel = isFixedFinalLane
            ? getFixedFinalPricingMathLabel({
                pricingMode: lanePricingMode,
                appliedSellRateRule,
                marginType: laneMarginType,
                marginValue: laneMarginValue,
                pricingFormula: lanePricingFormula,
              })
            : null;

          const tooltipContent = laneError
            ? `Lane ${laneNumber} failed: ${laneError}.`
            : getLaneTooltipContent({
                buyRate,
                originalBuyRate,
                buyRateRules,
                buyRateRangeLow,
                buyRateRangeHigh,
                transportType,
                selectedQuoteSource,
                sellRate: sellLane?.sellRate,
                sellRateRounded: sellLane?.sellRateRounded,
                roundingRules: laneRoundingRules,
                marginType: laneMarginType,
                marginValue: laneMarginValue,
                pricingFormula: lanePricingFormula,
                pricingMode: lanePricingMode,
                appliedSellRateRule,
                distanceSource: lane.distanceSource,
              });

          return (
            <Flex
              key={`${lane.quoteRequestId}-${index}`}
              direction='col'
              gap='xs'
              className='py-1 rounded-sm pr-1'
              tabIndex={0}
              aria-label={`Lane ${laneNumber} details. ${route ? `${route}. ` : ''}${tooltipContent}`}
            >
              <Flex direction='col' gap='none'>
                <Flex align='center' gap='xs'>
                  <Typography
                    variant='body-xs'
                    weight='semibold'
                    as='span'
                    className='text-info-600'
                  >
                    {transportType ? toTitleCase(transportType) : 'Transport'}
                  </Typography>
                  {!laneError && (
                    <Typography
                      variant='body-xs'
                      textColor='lessMuted'
                      className='tracking-wide flex flex-row items-center gap-0.5'
                    >
                      <LaneSummaryText
                        isFixedFinalLane={isFixedFinalLane}
                        selectedQuoteSource={selectedQuoteSource}
                        buyRate={buyRate}
                        hasSellRate={hasSellRate}
                        sellRateValue={sellRateValue}
                      />
                      <Tooltip delayDuration={10}>
                        <TooltipTrigger asChild>
                          <InfoIcon className='w-3.5 h-3.5 ml-1 cursor-help' />
                        </TooltipTrigger>
                        <LaneDetailsTooltip
                          route={route}
                          tooltipContent={tooltipContent}
                          buyRate={buyRate}
                          originalBuyRate={originalBuyRate}
                          selectedQuoteSource={selectedQuoteSource}
                          buyRateRangeLow={buyRateRangeLow}
                          buyRateRangeHigh={buyRateRangeHigh}
                          buyRateRules={buyRateRules}
                          buyRateBreakdownState={buyRateBreakdownState}
                          hasSellRate={hasSellRate}
                          sellRate={sellLane?.sellRate}
                          sellRateRounded={sellLane?.sellRateRounded}
                          appliedSellRateRule={appliedSellRateRule}
                          marginType={laneMarginType}
                          marginValue={laneMarginValue}
                          pricingFormula={lanePricingFormula}
                          pricingMode={lanePricingMode}
                          roundingRules={laneRoundingRules}
                        />
                      </Tooltip>
                    </Typography>
                  )}
                </Flex>

                <LaneRouteSummary
                  route={route}
                  routeShort={routeShort}
                  hasMultipleStops={hasMultipleStops}
                />
                {isFixedFinalLane && hasSellRate && fixedFinalMathLabel && (
                  <Typography variant='body-xs' textColor='lessMuted'>
                    {fixedFinalMathLabel} ={' '}
                    {formatDollarsNoCents(sellLane?.sellRate ?? sellRateValue)}
                    {sellLane?.sellRateRounded != null &&
                      sellLane.sellRate != null &&
                      sellLane.sellRateRounded !== sellLane.sellRate &&
                      `, rounded to ${formatDollarsNoCents(sellLane.sellRateRounded)}`}
                  </Typography>
                )}
              </Flex>

              {laneError && (
                <Typography variant='body-xs' className='text-error-main'>
                  Quote unavailable {laneError ? `(${laneError})` : ''}
                </Typography>
              )}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};
